"use client"

import { useState, useEffect } from "react"
import Layout from "../components/Layout"
import Card from "../components/Card"
import Button from "../components/Button"
import Alert from "../components/Alert"
import { Save, ChevronDown, ChevronRight, Lock, AlertTriangle, Mail, Bell, User, X } from "react-feather"

const Settings = () => {
  const [settings, setSettings] = useState({
    email: "",
    notifications: true,
    darkMode: false,
    language: "english",
  })

  const [accountStatus, setAccountStatus] = useState(null)
  const API_BASE_URL = 'https://api.tenarx.net';

  useEffect(() => {
    const token = localStorage.getItem("userToken")
    if (!token) return
    const fetchData = async () => {
      try {
        // basic info
        const resInfo = await fetch(`${API_BASE_URL}/api/auth/basic-info`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        const dataInfo = await resInfo.json()
        if (resInfo.ok && dataInfo?.success) {
          setSettings((prev) => ({ ...prev, email: dataInfo.data.email || "" }))
        }
        // account status
        const resStatus = await fetch(`${API_BASE_URL}/api/auth/account/status`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        const dataStatus = await resStatus.json()
        if (resStatus.ok && dataStatus?.success) {
          setAccountStatus({ status: dataStatus.accountStatus, role: dataStatus.role })
        }
      } catch (e) {
        // Non-blocking: show a small alert
        setAlert({ type: "error", message: e.message || "Failed to load settings" })
      }
    }
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [password, setPassword] = useState({
    current: "",
    new: "",
    confirm: "",
  })

  const [alert, setAlert] = useState(null)
  const [showPasswordSection, setShowPasswordSection] = useState(false)
  const [showDangerZone, setShowDangerZone] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const handleSettingsChange = (e) => {
    const { name, value, type, checked } = e.target
    setSettings({
      ...settings,
      [name]: type === "checkbox" ? checked : value,
    })
  }

  const handlePasswordChange = (e) => {
    const { name, value } = e.target
    setPassword({
      ...password,
      [name]: value,
    })
  }

  const saveSettings = async () => {
    try {
      const token = localStorage.getItem("userToken")
      if (!token) {
        setAlert({ type: "error", message: "You must be logged in" })
        return
      }
      const res = await fetch(`${API_BASE_URL}/api/auth/basic-info`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ email: settings.email }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.message || "Failed to save settings")
      setAlert({ type: "success", message: "Settings updated successfully!" })
    } catch (e) {
      setAlert({ type: "error", message: e.message || "Failed to save settings" })
    }
  }

  const changePassword = async (e) => {
    e.preventDefault()

    if (password.new !== password.confirm) {
      setAlert({ type: "error", message: "New passwords don't match" })
      return
    }
    if (!password.current || password.new.length < 6) {
      setAlert({ type: "error", message: "Password must be at least 6 characters and current required" })
      return
    }
    try {
      const token = localStorage.getItem("userToken")
      if (!token) {
        setAlert({ type: "error", message: "You must be logged in" })
        return
      }
      const res = await fetch(`${API_BASE_URL}/api/auth/change-password`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ current: password.current, new: password.new, confirm: password.confirm }),
      })
      const data = await res.json()
      if (!res.ok || !data.success) throw new Error(data.message || "Failed to change password")
      setAlert({ type: "success", message: "Password changed successfully!" })
      setPassword({ current: "", new: "", confirm: "" })
      setShowPasswordSection(false)
    } catch (e) {
      setAlert({ type: "error", message: e.message || "Failed to change password" })
    }
  }

  const handleDeleteAccount = () => {
    // This would typically call an API endpoint to delete the account
    setAlert({
      type: "info",
      message: "Account deletion would be processed here",
    })
    setShowDeleteConfirm(false)
  }

  return (
    <Layout title="Settings">
      {alert && (
        <Alert type={alert.type} dismissible onDismiss={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      <div className="max-w-4xl mx-auto">
        <Card className="mb-6">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
              <User className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Account Settings</h2>
              <p className="text-sm text-gray-500">Manage your account preferences and settings</p>
            </div>
          </div>

          <div className="space-y-6">
            <div className="border-b pb-6">
              <h3 className="text-lg font-medium mb-4 flex items-center">
                <Mail className="w-5 h-5 mr-2 text-blue-500" />
                Email Settings
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                  <div className="flex">
                    <input
                      type="email"
                      name="email"
                      value={settings.email}
                      onChange={handleSettingsChange}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                    <Button variant="secondary" className="ml-2" onClick={saveSettings}>Save</Button>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-b pb-6">
              <h3 className="text-lg font-medium mb-4 flex items-center">
                <Bell className="w-5 h-5 mr-2 text-purple-500" />
                Notifications
              </h3>
              <div className="space-y-4">
                <div className="text-sm text-gray-600">Notifications preferences are currently managed globally.</div>
              </div>
            </div>

            <div className="pt-4 flex justify-between items-center">
              {accountStatus && (
                <div className="text-sm text-gray-600">
                  <span className="mr-2">Role: <span className="font-medium capitalize">{accountStatus.role}</span></span>
                  <span>Status: <span className={`px-2 py-0.5 rounded text-white ${accountStatus.status === 'active' ? 'bg-green-600' : accountStatus.status === 'pending_approval' ? 'bg-yellow-600' : 'bg-red-600'}`}>{accountStatus.status.replace('_',' ')}</span></span>
                </div>
              )}
              <Button onClick={saveSettings} className="flex items-center">
                <Save className="w-4 h-4 mr-2" />
                Save Settings
              </Button>
            </div>
          </div>
        </Card>

        {/* Security Settings - Collapsed by default */}
        <Card className="mb-6">
          <button
            onClick={() => setShowPasswordSection(!showPasswordSection)}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                <Lock className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium">Security Settings</h3>
                <p className="text-sm text-gray-500">Change your password and security preferences</p>
              </div>
            </div>
            {showPasswordSection ? (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-500" />
            )}
          </button>

          {showPasswordSection && (
            <div className="mt-6 pt-6 border-t">
              <form onSubmit={changePassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                  <input
                    type="password"
                    name="current"
                    value={password.current}
                    onChange={handlePasswordChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                  <input
                    type="password"
                    name="new"
                    value={password.new}
                    onChange={handlePasswordChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                  <input
                    type="password"
                    name="confirm"
                    value={password.confirm}
                    onChange={handlePasswordChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div className="pt-3 flex justify-end">
                  <Button type="submit">Change Password</Button>
                </div>
              </form>
            </div>
          )}
        </Card>

        {/* Danger Zone - Collapsed by default */}
        <Card className="border-red-200">
          <button
            onClick={() => setShowDangerZone(!showDangerZone)}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-red-700">Danger Zone</h3>
                <p className="text-sm text-gray-500">Irreversible account actions</p>
              </div>
            </div>
            {showDangerZone ? (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-500" />
            )}
          </button>

          {showDangerZone && (
            <div className="mt-6 pt-6 border-t border-red-200">
              <div className="bg-red-50 p-4 rounded-lg">
                <h4 className="text-md font-medium text-red-800 mb-2">Delete Account</h4>
                <p className="text-sm text-red-700 mb-4">
                  Permanently delete your account and all of your data. This action cannot be undone.
                </p>
                <Button variant="danger" onClick={() => setShowDeleteConfirm(true)}>
                  Delete Account
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Delete Account Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-red-700 flex items-center">
                <AlertTriangle className="w-5 h-5 mr-2" />
                Delete Account
              </h3>
              <button onClick={() => setShowDeleteConfirm(false)} className="text-gray-400 hover:text-gray-500">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-gray-700 mb-4">
                Are you absolutely sure you want to delete your account? This action cannot be undone and all your data
                will be permanently removed.
              </p>
              <div className="bg-yellow-50 p-3 rounded-md text-sm text-yellow-700 mb-4">
                <p>To confirm, please type "delete my account" below:</p>
                <input
                  type="text"
                  className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                  placeholder="delete my account"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleDeleteAccount}>
                Permanently Delete Account
              </Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  )
}

export default Settings
